import {
  OrganizationType
} from '../../../../../models/OrganizationType'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box, CircularProgress,
  FormControl,
  InputLabel, MenuItem,
  Select, type SelectChangeEvent, TextField,
  Typography,
  InputAdornment,
  Alert
} from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'
import {
  AddIsdDistrictProgramEnhancement,
  UpdateIsdDistrictProgramEnhancement
} from '../../../../../services/AnnualPlanService'
import { ActivityEditSession } from '../ActivityEditSession'
import { PriorityFourActivityName } from '../../../../../models/PriorityFourActivityName'
import {
  useEEMDistrictAndBuildingsOptions
} from '../../../../../hooks/use-eemDistrictBuildingOptions'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { budgetNeedsReview } from '../../../../../utils/budgetUtil'
import { OrganizationTypeSelector } from '../OrganizationTypeSelector'

export interface AddIsdDistrictProgramEnhancementFormValues {
  priorityId: number
  organizationType: OrganizationType | null
  organizationId: number | null
  organizationContactName: string
  organizationContactEmail: string
  actions: string
  resources: string
  budgetRequest: number | null
  isImplementationPhase?: boolean
  needsReview?: boolean
}

export interface UpdateIsdDistrictProgramEnhancementFormValues
  extends AddIsdDistrictProgramEnhancementFormValues {
  id: number
  isUpdate: boolean
  currentProgress?: string[] | null
  actualAmount?: number | null
}

interface Props {
  open: boolean
  priorityId: number
  planOrgId: number
  isdOrgId: number
  eemId?: number | null
  initialValues?: UpdateIsdDistrictProgramEnhancementFormValues
  onClose: (isUpdate?: boolean) => void
  onSaved: () => void
  implementationPhase?: boolean
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const IsdDistrictProgramEnhancementModal = (props: Props): JSX.Element => {
  const hasInitialValues = props?.initialValues != null
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')

  const [
    formValues,
    setFormValues
  ] = useState<AddIsdDistrictProgramEnhancementFormValues>({
    priorityId: props.priorityId ?? 0,
    organizationType: null,
    organizationId: props.eemId ?? props.initialValues?.organizationId ?? null,
    organizationContactName: '',
    organizationContactEmail: '',
    actions: '',
    resources: '',
    budgetRequest: null,
    isImplementationPhase: props.implementationPhase
  })
  const [submitted, setIsSubmitted] = useState(false)
  const {
    districtAndBuildingsOptions: districts,
    isLoading: isLoadingDistricts
  } = useEEMDistrictAndBuildingsOptions(props.planOrgId)

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues({
        ...props.initialValues,
        isImplementationPhase: props.implementationPhase
      })
    }
  }, [props.initialValues])

  const formValidators: Record<keyof AddIsdDistrictProgramEnhancementFormValues, { required: boolean, isValid: boolean }> = {
    priorityId: { required: true, isValid: formValues.priorityId === props.priorityId },
    organizationType: { required: true, isValid: formValues.organizationType != null && Object.values(OrganizationType).includes(formValues.organizationType) },
    organizationId: { required: true, isValid: formValues.organizationId != null && (formValues.organizationType === OrganizationType.Isd ? formValues.organizationId === props.eemId : formValues.organizationId > 0) },
    organizationContactName: { required: true, isValid: formValues.organizationContactName.trim() !== '' },
    organizationContactEmail: { required: true, isValid: formValues.organizationContactEmail.trim() !== '' },
    actions: { required: true, isValid: formValues.actions.trim() !== '' },
    resources: { required: true, isValid: formValues.resources.trim() !== '' },
    budgetRequest: { required: true, isValid: isValidPositiveNumber(formValues.budgetRequest) },
    isImplementationPhase: { required: true, isValid: formValues.isImplementationPhase === props.implementationPhase },
    needsReview: { required: false, isValid: true }
  }

  const invalidFormValues = Object.entries(formValidators)
    .filter(([_, validator]) => validator.required && !validator.isValid)
    .map(([fieldName, _]) => fieldName)

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleOrganizationTypeUpdate = (organizationType: OrganizationType, organizationId: number | null): void => {
    setFormValues({ ...formValues, organizationType, organizationId })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.implementationPhase === true && props.initialValues != null && !props.initialValues.isUpdate) {
      if (budgetNeedsReview(props.initialValues.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }

    await continueImplementationSubmit()
  }

  const continueImplementationSubmit = async (): Promise<void> => {
    setIsSubmitted(true)

    if (invalidFormValues.length > 0) {
      toast.error('Please fill out required fields')
      return
    }

    const formValuesToSubmit: AddIsdDistrictProgramEnhancementFormValues = {
      priorityId: props.priorityId,
      organizationType: formValues.organizationType,
      organizationId: formValues.organizationType === OrganizationType.District
        ? formValues.organizationId
        : props.eemId ?? null,
      organizationContactName: formValues.organizationContactName,
      organizationContactEmail: formValues.organizationContactEmail,
      actions: formValues.actions,
      resources: formValues.resources,
      budgetRequest: formValues.budgetRequest,
      isImplementationPhase: formValues.isImplementationPhase ?? props.implementationPhase,
      needsReview: confirmState === 'confirmed'
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateIsdDistrictProgramEnhancement({
        ...formValuesToSubmit,
        id: props.initialValues.id,
        isUpdate: props.initialValues.isUpdate
      })
      : await AddIsdDistrictProgramEnhancement(formValuesToSubmit)

    if (!success) {
      errors.forEach(error => toast.error(error))
      return
    }

    toast.success(
      props.implementationPhase === true
        ? 'Enhancement Plan Updated'
        : 'Enhancement Plan Saved'
    )

    props.onSaved()
    props.onClose(props.initialValues?.isUpdate ?? false)
  }

  const isDistrict = formValues.organizationType === OrganizationType.District

  const getDataTestId = (): string => {
    if (props.implementationPhase === true) {
      return 'updateIsdDistrictProgramEnhancementModal'
    }
    return hasInitialValues
      ? 'editIsdDistrictProgramEnhancementModal'
      : 'addIsdDistrictProgramEnhancementModal'
  }

  const disableEditing = hasInitialValues && props.implementationPhase !== true

  const getTitle = (): string => {
    let prefix = ''
    if (props.implementationPhase === true) prefix = 'Update '
    return `${prefix}ISD/District Program Enhancements`
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose(props.initialValues?.isUpdate ?? false)
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueImplementationSubmit()
        }}
    />
    : <Modal
        title={getTitle()}
        open={props.open}
        data-testid={getDataTestId()}
        width='544px'
        maxWidth='md'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={() => { props.onClose(props.initialValues?.isUpdate ?? false) }}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {
              props.implementationPhase === true && props.initialValues == null &&
                <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                  backgroundColor: 'var(--blue-500)',
                  color: 'white',
                  fill: 'white'
                }} severity="info"
                >The addition of this item will prompt a review from the START team.
                </Alert>
            }
            <Typography variant='h5'>Location</Typography>
            <OrganizationTypeSelector
              disabled={disableEditing}
              required={formValidators.organizationType.required}
              error={submitted && !formValidators.organizationType.isValid}
              value={formValues.organizationType}
              isdEemId={props.eemId}
              onChange={handleOrganizationTypeUpdate}
            />

            <Typography variant='h5'>{formValues.organizationType}</Typography>

            {isDistrict &&
              <>
                {isLoadingDistricts
                  ? <CircularProgress sx={{ alignSelf: 'center' }} />
                  : <FormControl
                      fullWidth
                      disabled={disableEditing}
                      required={formValidators.organizationId.required}
                      error={submitted && !formValidators.organizationId.isValid}
                  >
                    <InputLabel id="district-label">Select District</InputLabel>
                    <Select
                      labelId="district-label"
                      id="organizationId"
                      value={districts.some(d => d.id === formValues.organizationId) ? formValues.organizationId : ''}
                      label="Select District"
                      name='organizationId'
                      data-testid='district'
                      onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.organizationId>) => void}
                      disabled={disableEditing}
                    >
                      {districts.length === 0 && <Typography variant='body1' textAlign={'center'}>No district options</Typography>}
                      {districts.length > 0 && districts.map((district) => (
                        <MenuItem key={district.id} value={district.id} data-testid='districtOption'>{district.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
              </>}

            <TextField
              name='organizationContactName'
              label={`${formValues.organizationType ?? ''} Contact Name`}
              value={formValues.organizationContactName}
              data-testid='organizationContactName'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValidators.organizationContactName.required}
              error={submitted && !formValidators.organizationContactName.isValid}
            />

            <TextField
              name='organizationContactEmail'
              label={`${formValues.organizationType ?? ''} Contact Email`}
              value={formValues.organizationContactEmail}
              data-testid='organizationContactEmail'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValidators.organizationContactEmail.required}
              error={submitted && !formValidators.organizationContactEmail.isValid}
            />

            <TextField
              name='actions'
              label={<>What <span style={{ fontWeight: 700 }}>actions</span> are planned?</>}
              value={formValues.actions}
              data-testid='actions'
              onChange={handleFieldUpdate}
              variant='filled'
              multiline={true}
              rows={4}
              required={formValidators.actions.required}
              error={submitted && !formValidators.actions.isValid}
            />

            <TextField
              name='resources'
              label={<>What <span style={{ fontWeight: 700 }}>resources</span> are needed?</>}
              value={formValues.resources}
              data-testid='resources'
              onChange={handleFieldUpdate}
              variant='filled'
              multiline={true}
              rows={4}
              required={formValidators.resources.required}
              error={submitted && !formValidators.resources.isValid}
            />

            <Typography variant='body1'>Does this require additional funding to complete?</Typography>

            <TextField
              id='budget-request'
              sx={{ mt: '.75em', width: '250px' }}
              variant='filled'
              onChange={handleFieldUpdate}
              label={'Budget Request'}
              data-testid='budgetRequest'
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              name='budgetRequest'
              required={formValidators.budgetRequest.required}
              error={submitted && !formValidators.budgetRequest.isValid}
              value={formValues.budgetRequest ?? ''}
            />

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues.id}
                activityName={PriorityFourActivityName.ProgramEnrichment}
                onClose={() => { props.onClose(props.initialValues?.isUpdate ?? false) }}
              />
            }
          </Box>
        }
    />)
}
